import React from 'react';

import { Typography, Box, Divider, Button } from '@mui/material';

import { useAppSelector, useAppDispatch } from 'hooks';
import { sendUpdateAlertAck } from 'state/alerts';
import { AlertManager } from 'models';

interface OverviewProps {
    alert: AlertManager.Alert;
    onRefreshClick?: () => void;
    isRefreshing?: boolean;
    hideRefresh?: boolean;
}

const AlertOverview: React.FunctionComponent<OverviewProps> = ({ alert }) => {
    const dispatch = useAppDispatch();
    const { alertAckResponse } = useAppSelector((root) => root.alerts);

    const makeAlertTypeNamePretty = (type: string) => {
        return type
            .split('_')
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ');
    };

    let button: React.ReactNode;

    const handleAcknowledged = () => {
        let newAck = alert?.acknowledged;
        if (alert?.acknowledged) {
            newAck = false;
        } else {
            newAck = true;
        }
        if (alert && newAck !== undefined) {
            dispatch(
                sendUpdateAlertAck({
                    id: alert.id,
                    acknowledged: newAck,
                }),
            );
        }
    };

    if (alert?.acknowledged) {
        button = (
            <Button
                variant="outlined"
                color="success"
                size="small"
                //startIcon={<EventBusyIcon />}
                onClick={handleAcknowledged}
                disabled={
                    //stops the user from clicking on the button if loading, and disables button if communication has started (or on pending passes, has been ignored)
                    alertAckResponse.status === 'loading'
                }
            >
                Un-Acknowledge Alert
            </Button>
        );
    } else {
        button = (
            <Button
                variant="outlined"
                color="error"
                size="small"
                //startIcon={<EventAvailableIcon />}
                onClick={handleAcknowledged}
                disabled={alertAckResponse.status === 'loading'}
            >
                Acknowledge Alert
            </Button>
        );
    }

    return (
        <div>
            {alert && (
                <>
                    <Divider sx={{ my: 1 }} />
                    <Box
                        sx={{
                            my: 1,
                            py: 1,
                            paddingLeft: 0.5,
                        }}
                    >
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Alert Type: {makeAlertTypeNamePretty(alert.type)}
                        </Typography>
                        <Box
                            sx={{
                                //display: 'flex',
                                //alignItems: 'flex-start',
                                //justifyContent: 'flex-end',
                                //paddingRight: 18,
                                paddingBottom: 1,
                            }}
                        >
                            <Typography variant="subtitle2" align="left">
                                Alert was created on{' '}
                                {alert.created.toUTCString()}
                            </Typography>
                        </Box>
                    </Box>
                    <table
                        align="left"
                        border={0}
                        //cellSpacing={0}
                        cellPadding={4}
                        style={{ width: '100%', minWidth: '120px' }}
                    >
                        <tbody>
                            {alert.level && (
                                <tr>
                                    <td align="left">Level</td>
                                    <td align="left">{alert.level}</td>
                                </tr>
                            )}
                            {alert.start_time && (
                                <tr>
                                    <td align="left">Start Time</td>
                                    <td align="left">
                                        {alert.start_time.toUTCString()}
                                    </td>
                                </tr>
                            )}
                            {alert.stop_time && (
                                <tr>
                                    <td align="left">Stop Time</td>
                                    <td align="left">
                                        {alert.stop_time.toUTCString()}
                                    </td>
                                </tr>
                            )}
                            {alert.details && (
                                <tr>
                                    <td align="left">Details</td>
                                    <td align="left">{alert.details}</td>
                                </tr>
                            )}
                            {
                                <tr>
                                    <td align="left">Related Antennas</td>
                                    <td align="left">
                                        {alert.antenna ? alert.antenna : 'None'}
                                    </td>
                                </tr>
                            }
                            {
                                <tr>
                                    <td align="left">Related Satellites</td>
                                    <td align="left">
                                        {alert.satellite
                                            ? alert.satellite
                                            : 'None'}
                                    </td>
                                </tr>
                            }
                            {alert.acknowledged !== null && (
                                <tr>
                                    <td align="left">Acknowledged?</td>
                                    <td align="left">
                                        {alert.acknowledged ? 'Yes' : 'No'}
                                    </td>
                                </tr>
                            )}
                            {alert.cleared !== null && (
                                <tr>
                                    <td align="left">Cleared?</td>
                                    <td align="left">
                                        {alert.cleared ? 'Yes' : 'No'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    <Box
                        sx={{
                            my: 1,
                            py: 1,
                            paddingLeft: 0.5,
                        }}
                    >
                        {button}
                    </Box>
                </>
            )}
            {!alert && alert == null && (
                <Typography variant="h6" align="center" sx={{ m: 2 }}>
                    Cannot find Alert with Id {alert}
                </Typography>
            )}
        </div>
    );
};

AlertOverview.displayName = 'AlertOverview';
export default AlertOverview;
